<script setup lang="ts">
import { ref, watch } from 'vue';
import { updateDisplayName } from '@/api/endpoints/auth.js';
import { useAuthStore } from '@/stores/auth.js';
import Button from '@/components/atoms/Button.vue';
import Input from '@/components/atoms/Input.vue';
import Dialog from '@/components/molecules/Dialog.vue';

// The one account setting the app offers (HUBL-072): a single display-name field
// on the SD-053 dialog shell. It reads the current name from the auth store and
// hands the saved user straight back to the store, so the shell's identity header
// and avatar initials update with no `me` refetch. The shell owns `open` only —
// there is no user prop.
const props = defineProps<{ open: boolean }>();

const emit = defineEmits<{
  'update:open': [boolean];
}>();

const auth = useAuthStore();

// Mirrors the server's cap so the field can prevent over-length at the keyboard
// as well as report it on submit; the server stays authoritative.
const DISPLAY_NAME_MAX = 120;

const displayName = ref('');
const submitting = ref(false);
const error = ref<string | null>(null);

// Re-prefill on every open so a cancelled edit never survives into the next one
// (PRD story 15). A legacy null name prefills empty — never the email, which
// would quietly persist an address as somebody's display name.
watch(
  () => props.open,
  (open) => {
    if (!open) return;
    displayName.value = auth.user?.displayName ?? '';
    submitting.value = false;
    error.value = null;
  },
  { immediate: true }
);

async function onSubmit(): Promise<void> {
  if (submitting.value) return;
  error.value = null;
  // Client-side mirror of the server's trim/non-empty/cap rules, for fast inline
  // feedback; a failure here issues no request.
  const next = displayName.value.trim();
  if (next.length === 0) {
    error.value = 'Enter a display name.';
    return;
  }
  if (next.length > DISPLAY_NAME_MAX) {
    error.value = `Display name must be ${DISPLAY_NAME_MAX} characters or fewer.`;
    return;
  }
  submitting.value = true;
  try {
    auth.applyUser(await updateDisplayName({ displayName: next }));
    // The dialog closing is the confirmation — no success toast (PRD story 16).
    emit('update:open', false);
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Could not save your name.';
  } finally {
    submitting.value = false;
  }
}

// The single close path for Cancel, `Esc`, and outside click. It changes nothing
// server-side or in the store, and clears the field so the next open re-prefills
// from the store rather than showing the abandoned edit.
function onClose(): void {
  displayName.value = '';
  submitting.value = false;
  error.value = null;
  emit('update:open', false);
}
</script>

<template>
  <!-- Every other Dialog consumer passes a `description`, and Radix otherwise
       leaves the panel's aria-describedby pointing at nothing. -->
  <Dialog
    :open="open"
    title="Profile"
    description="The name teammates see on your comments and in member lists."
    @update:open="(v) => !v && onClose()"
  >
    <form class="profile-dialog__form" @submit.prevent="onSubmit">
      <div class="profile-dialog__field">
        <label class="profile-dialog__label" for="profile-display-name">
          Display name
        </label>
        <Input
          id="profile-display-name"
          v-model="displayName"
          class="profile-dialog__input"
          :maxlength="DISPLAY_NAME_MAX"
          :aria-invalid="error === null ? undefined : 'true'"
          :aria-describedby="error === null ? undefined : 'profile-display-name-error'"
        />
      </div>
      <p
        v-if="error"
        id="profile-display-name-error"
        role="alert"
        class="profile-dialog__error"
      >
        {{ error }}
      </p>
      <div class="profile-dialog__actions">
        <Button variant="secondary" @click="onClose">Cancel</Button>
        <Button type="submit" :loading="submitting">Save</Button>
      </div>
    </form>
  </Dialog>
</template>

<style scoped>
/*
 * The scrim, panel and title all come from the `Dialog` shell; only this form's
 * own layout lives here. Scoped is correct even though the panel is portaled —
 * slot content keeps this component's scope id (the InviteMemberModal pattern).
 * Semantic tokens only (SD-052).
 */
.profile-dialog__form {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.profile-dialog__field {
  display: flex;
  flex-direction: column;
}

.profile-dialog__label {
  margin-bottom: var(--space-1);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-strong);
}

.profile-dialog__input {
  width: 100%;
  padding: var(--space-2) var(--space-3);
}

.profile-dialog__error {
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-danger);
}

.profile-dialog__actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-2);
}
</style>
